import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import Layout from '../components/layout/Layout';
import { emailService } from '../services/emailService';

interface InstallationFormData {
  orderInfo?: {
    orderId: string;
    customerEmail: string;
    customerName: string;
  };
  hasCentralHVAC: string;
  iaqInterest: string;
  hasExistingEquipment: string;
  existingEquipmentDetails: string;
  hasPreferredContractor: string;
  contractorName: string;
  contractorContact: string;
}

const OrderConfirmationPage: React.FC = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState<InstallationFormData>({
    hasCentralHVAC: '',
    iaqInterest: '',
    hasExistingEquipment: '',
    existingEquipmentDetails: '',
    hasPreferredContractor: '',
    contractorName: '',
    contractorContact: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    // Check if coming from checkout
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('source');
    
    if (source === 'checkout') {
      // Get stored checkout info
      const storedInfo = localStorage.getItem('haven_checkout_info');
      if (storedInfo) {
        try {
          const checkoutInfo = JSON.parse(storedInfo);
          
          // Check if info is recent (within 10 minutes)
          const isRecent = (Date.now() - checkoutInfo.timestamp) < 10 * 60 * 1000;
          
          if (isRecent && checkoutInfo.hasProfessionalInstall) {
            // Auto-populate customer info from cart
            const customerName = checkoutInfo.cart?.customer?.firstName && checkoutInfo.cart?.customer?.lastName
              ? `${checkoutInfo.cart.customer.firstName} ${checkoutInfo.cart.customer.lastName}`
              : 'Professional Installation Customer';
              
            const customerEmail = checkoutInfo.cart?.customer?.email || '';
            
            // Update URL with customer info
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set('name', customerName);
            newUrl.searchParams.set('email', customerEmail);
            newUrl.searchParams.set('product', 'Professionally Installed HAVEN Central Air Bundle');
            newUrl.searchParams.set('source', 'checkout');
            
            // Update URL without page reload
            window.history.replaceState({}, '', newUrl.toString());
          }
          
          // Clean up stored info
          localStorage.removeItem('haven_checkout_info');
        } catch (error) {
          console.error('Error parsing checkout info:', error);
          localStorage.removeItem('haven_checkout_info');
        }
      }
    }
  }, []);

  const handleInputChange = (field: keyof InstallationFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: {[key: string]: string} = {};
    
    switch (step) {
      case 0:
        if (!formData.hasCentralHVAC) {
          newErrors.hasCentralHVAC = 'Please select an option';
        }
        break;
      case 1:
        if (!formData.iaqInterest.trim()) {
          newErrors.iaqInterest = 'Please describe your interest in IAQ';
        }
        break;
      case 2:
        if (!formData.hasExistingEquipment) {
          newErrors.hasExistingEquipment = 'Please select an option';
        }
        if (formData.hasExistingEquipment === 'yes' && !formData.existingEquipmentDetails.trim()) {
          newErrors.existingEquipmentDetails = 'Please describe your existing equipment';
        }
        break;
      case 3:
        if (!formData.hasPreferredContractor) {
          newErrors.hasPreferredContractor = 'Please select an option';
        }
        if (formData.hasPreferredContractor === 'yes') {
          if (!formData.contractorName.trim()) {
            newErrors.contractorName = 'Please enter contractor name';
          }
          if (!formData.contractorContact.trim()) {
            newErrors.contractorContact = 'Please enter contractor contact information';
          }
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    const newErrors: any = {};

    // Validate required fields
    if (!formData.hasCentralHVAC) {
      newErrors.hasCentralHVAC = 'Please select an option';
    }

    if (!formData.iaqInterest.trim()) {
      newErrors.iaqInterest = 'Please describe your interest in IAQ';
    }

    if (!formData.hasExistingEquipment) {
      newErrors.hasExistingEquipment = 'Please select an option';
    }

    if (formData.hasExistingEquipment === 'yes' && !formData.existingEquipmentDetails.trim()) {
      newErrors.existingEquipmentDetails = 'Please describe your existing equipment';
    }

    if (!formData.hasPreferredContractor) {
      newErrors.hasPreferredContractor = 'Please select an option';
    }

    if (formData.hasPreferredContractor === 'yes') {
      if (!formData.contractorName.trim()) {
        newErrors.contractorName = 'Please enter contractor name';
      }
      if (!formData.contractorContact.trim()) {
        newErrors.contractorContact = 'Please enter contractor contact information';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Get URL parameters for order info
      const urlParams = new URLSearchParams(window.location.search);
      const orderNumber = urlParams.get('order') || urlParams.get('order_id') || undefined;
      const customerName = urlParams.get('name') || 'Unknown Customer';
      const customerEmail = urlParams.get('email') || '';
      const productName = urlParams.get('product') || 'Professional Installation Product';

      // Format data for email service (updated interface)
      const emailData = {
        orderNumber,
        customerName,
        customerEmail,
        productName,
        hasCentralHVAC: formData.hasCentralHVAC === 'yes',
        iaqInterests: formData.iaqInterest,
        hasExistingEquipment: formData.hasExistingEquipment === 'yes',
        existingEquipmentDetails: formData.hasExistingEquipment === 'yes' ? formData.existingEquipmentDetails : undefined,
        hasPreferredContractor: formData.hasPreferredContractor === 'yes',
        contractorContact: formData.hasPreferredContractor === 'yes' ? formData.contractorContact : undefined,
      };

      const success = await emailService.sendInstallationForm(emailData);
      
      if (success) {
        setIsSubmitted(true);
      } else {
        throw new Error('Failed to send installation details');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ 
        submit: 'There was an error submitting your information. Please try again or contact support.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    'HVAC System',
    'IAQ Interest', 
    'Existing Equipment',
    'Preferred Contractor'
  ];

  // Check if this is a post-purchase flow
  const urlParams = new URLSearchParams(window.location.search);
  const isPostPurchase = urlParams.get('source') === 'checkout';
  const customerName = urlParams.get('name') || '';

  if (isSubmitted) {
    return (
      <Layout>
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 3 }} />
            <Typography variant="h4" gutterBottom color="success.main" fontWeight="bold">
              Installation Details Submitted!
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Thank you for providing your installation requirements.
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.6 }}>
              Our team will contact you within <strong>1 week</strong> to coordinate your professional installation.
              We'll match you with qualified HVAC professionals in your area and help schedule your installation appointment.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                href="/homeowner-store"
                sx={{ minWidth: 150 }}
              >
                Continue Shopping
              </Button>
              <Button
                variant="outlined"
                href="/"
                sx={{ minWidth: 150 }}
              >
                Back to Home
              </Button>
            </Box>
          </Paper>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4 }}>
          {/* Header with post-purchase context */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            {isPostPurchase ? (
              <>
                <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
                  🎉 Thank you for your purchase{customerName ? `, ${customerName.split(' ')[0]}` : ''}!
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  Complete your professional installation setup
                </Typography>
                <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                  <strong>Next Step:</strong> Please answer a few quick questions about your home setup. 
                  This helps us ensure your HAVEN system is installed correctly and works perfectly with your existing HVAC system.
                </Alert>
              </>
            ) : (
              <>
                <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
                  Professional Installation Requirements
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Help us coordinate your HAVEN installation
                </Typography>
              </>
            )}
          </Box>

          {/* Progress Stepper */}
          <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Question 1: Central HVAC */}
          {currentStep === 0 && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <FormControl component="fieldset" fullWidth error={!!errors.hasCentralHVAC}>
                  <FormLabel component="legend" sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 2 }}>
                    Question 1: Do you have a central ducted HVAC system?
                  </FormLabel>
                  <RadioGroup
                    value={formData.hasCentralHVAC}
                    onChange={(e) => handleInputChange('hasCentralHVAC', e.target.value)}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                  {errors.hasCentralHVAC && (
                    <Typography color="error" variant="caption">
                      {errors.hasCentralHVAC}
                    </Typography>
                  )}
                </FormControl>
              </CardContent>
            </Card>
          )}

          {/* Question 2: IAQ Interest */}
          {currentStep === 1 && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <FormLabel component="legend" sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 2, display: 'block' }}>
                  Question 2: What is your interest in IAQ?
                </FormLabel>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.iaqInterest}
                  onChange={(e) => handleInputChange('iaqInterest', e.target.value)}
                  placeholder="Tell us about your concerns or goals for indoor air quality..."
                  error={!!errors.iaqInterest}
                  helperText={errors.iaqInterest}
                />
              </CardContent>
            </Card>
          )}

          {/* Question 3: Existing Equipment */}
          {currentStep === 2 && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <FormControl component="fieldset" fullWidth error={!!errors.hasExistingEquipment}>
                  <FormLabel component="legend" sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 2 }}>
                    Question 3: Do you have existing whole-home humidifiers, dehumidifiers, ventilation or filtration equipment you would like to control?
                  </FormLabel>
                  <RadioGroup
                    value={formData.hasExistingEquipment}
                    onChange={(e) => handleInputChange('hasExistingEquipment', e.target.value)}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                  {errors.hasExistingEquipment && (
                    <Typography color="error" variant="caption">
                      {errors.hasExistingEquipment}
                    </Typography>
                  )}
                </FormControl>

                {formData.hasExistingEquipment === 'yes' && (
                  <Box sx={{ mt: 3 }}>
                    <FormLabel sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
                      What IAQ solutions do you already have installed?
                    </FormLabel>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={formData.existingEquipmentDetails}
                      onChange={(e) => handleInputChange('existingEquipmentDetails', e.target.value)}
                      placeholder="Please describe your existing equipment (humidifiers, air purifiers, ventilation systems, etc.)"
                      error={!!errors.existingEquipmentDetails}
                      helperText={errors.existingEquipmentDetails}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Question 4: Preferred Contractor */}
          {currentStep === 3 && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <FormControl component="fieldset" fullWidth error={!!errors.hasPreferredContractor}>
                  <FormLabel component="legend" sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 2 }}>
                    Question 4: Do you already have a professional HVAC contractor you would prefer to work with?
                  </FormLabel>
                  <RadioGroup
                    value={formData.hasPreferredContractor}
                    onChange={(e) => handleInputChange('hasPreferredContractor', e.target.value)}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                  {errors.hasPreferredContractor && (
                    <Typography color="error" variant="caption">
                      {errors.hasPreferredContractor}
                    </Typography>
                  )}
                </FormControl>

                {formData.hasPreferredContractor === 'yes' && (
                  <Box sx={{ mt: 3 }}>
                    <TextField
                      fullWidth
                      label="Contractor Name"
                      value={formData.contractorName}
                      onChange={(e) => handleInputChange('contractorName', e.target.value)}
                      error={!!errors.contractorName}
                      helperText={errors.contractorName}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Contact Information"
                      value={formData.contractorContact}
                      onChange={(e) => handleInputChange('contractorContact', e.target.value)}
                      placeholder="Phone number, email, or company name"
                      error={!!errors.contractorContact}
                      helperText={errors.contractorContact}
                    />
                  </Box>
                )}

                {formData.hasPreferredContractor === 'no' && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Great! We'll find a qualified HVAC professional in your area.
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              onClick={handleBack}
              disabled={currentStep === 0}
              variant="outlined"
            >
              Back
            </Button>
            
            {currentStep < steps.length - 1 ? (
              <Button
                onClick={handleNext}
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.secondary.main,
                  color: 'black',
                  '&:hover': {
                    backgroundColor: theme.palette.secondary.dark,
                  },
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  backgroundColor: theme.palette.secondary.main,
                  color: 'black',
                  '&:hover': {
                    backgroundColor: theme.palette.secondary.dark,
                  },
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Submitting...
                  </>
                ) : (
                  'Submit Installation Details'
                )}
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Layout>
  );
};

export default OrderConfirmationPage; 